import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, RefreshCw, Shield, Brain, Zap, Heart, Eye, Scale, Sun, Compass } from 'lucide-react';
import ShareResult from '../ShareResult';

const STOIC_PRINCIPLES = [
  { id: 'control', label: 'Focus of Control', icon: Compass, question: 'When something goes wrong, I focus on what I can change rather than what I can\'t.', color: '#00F0FF' },
  { id: 'calm', label: 'Calm Under Pressure', icon: Shield, question: 'I remain composed during stressful situations instead of reacting emotionally.', color: '#E63946' },
  { id: 'virtue', label: 'Virtue Over Pleasure', icon: Scale, question: 'I make decisions based on what is right rather than what feels good in the moment.', color: '#22c55e' },
  { id: 'acceptance', label: 'Acceptance', icon: Sun, question: 'I accept setbacks as natural and use them as fuel for growth.', color: '#f59e0b' },
  { id: 'rational', label: 'Rational Thinking', icon: Brain, question: 'I pause and think logically before acting on my emotions.', color: '#818cf8' },
  { id: 'mortality', label: 'Memento Mori', icon: Eye, question: 'I live each day with purpose, aware that life is finite.', color: '#a78bfa' },
  { id: 'resilience', label: 'Resilience', icon: Zap, question: 'I view hardships as opportunities to practice strength and patience.', color: '#E63946' },
  { id: 'compassion', label: 'Compassion', icon: Heart, question: 'I treat others with kindness and fairness, even when it\'s difficult.', color: '#00F0FF' },
];

const OPTIONS = [
  { label: 'Rarely', score: 1 },
  { label: 'Sometimes', score: 2 },
  { label: 'Often', score: 3 },
  { label: 'Almost Always', score: 4 },
];

const TIERS = [
  { min: 0, max: 12, label: 'Apprentice', desc: 'You\'re at the beginning of your stoic journey. Small daily practices will build a strong foundation.', color: '#71717a' },
  { min: 13, max: 20, label: 'Student', desc: 'You\'re developing stoic awareness. Keep pushing past emotional reactions.', color: '#f59e0b' },
  { min: 21, max: 27, label: 'Practitioner', desc: 'Solid stoic habits. You handle most situations with rational composure.', color: '#00F0FF' },
  { min: 28, max: 32, label: 'Sage', desc: 'Impressive mastery. Marcus Aurelius would approve.', color: '#22c55e' },
];

const DAILY_PRACTICES = [
  { title: 'Negative Visualization', desc: 'Spend 5 minutes imagining losing something you value. Appreciate it more deeply today.', category: 'Mental' },
  { title: 'Voluntary Discomfort', desc: 'Take a cold shower, skip a meal, or do something physically uncomfortable today.', category: 'Physical' },
  { title: 'Evening Reflection', desc: 'Before bed, review your day: What did I do well? Where did I fail? What can I improve?', category: 'Mental' },
  { title: 'Control Audit', desc: 'Write down 3 things bothering you. Cross out everything outside your control. Act on the rest.', category: 'Action' },
  { title: 'Kindness Challenge', desc: 'Help one person today without expecting anything in return. Practice virtue in action.', category: 'Social' },
  { title: 'Digital Stoicism', desc: 'Spend 2 hours today without checking your phone. Observe what emotions arise.', category: 'Mental' },
  { title: 'Obstacle is the Way', desc: 'Identify your biggest frustration today. Reframe it as a training opportunity.', category: 'Mental' },
];

export default function StoicPersonArticle() {
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [dailyPractice, setDailyPractice] = useState(() => DAILY_PRACTICES[Math.floor(Math.random() * DAILY_PRACTICES.length)]);

  const score = Object.values(answers).reduce((s, v) => s + v, 0);
  const maxScore = STOIC_PRINCIPLES.length * 4;
  const tier = TIERS.find(t => score >= t.min && score <= t.max) || TIERS[0];
  const allAnswered = Object.keys(answers).length === STOIC_PRINCIPLES.length;

  const refreshPractice = () => {
    let next;
    do { next = DAILY_PRACTICES[Math.floor(Math.random() * DAILY_PRACTICES.length)]; } while (next.title === dailyPractice.title && DAILY_PRACTICES.length > 1);
    setDailyPractice(next);
  };

  return (
    <main className="pt-20 min-h-screen bg-[#0a0a0a]" data-testid="stoic-person-article">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link to="/blog" className="text-zinc-500 text-xs uppercase tracking-wider hover:text-white transition-colors mb-4 inline-block" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Back to Blog</Link>

        <h1 className="text-4xl sm:text-5xl text-white mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif' }} data-testid="stoic-article-title">What is a Stoic Person?</h1>
        <p className="text-zinc-400 text-lg mb-8 leading-relaxed">Stoicism is not just philosophy — it's a way of life. Originating in Ancient Greece, it teaches us how to live virtuously, with purpose and composure.</p>

        {/* Article Content */}
        <div className="prose-ninja mb-12 space-y-6 text-zinc-300 leading-relaxed">
          <h2 className="text-white text-2xl" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>The Origins of Stoicism</h2>
          <p>Stoicism began in Athens around 300 BCE with Zeno of Citium. Over time, its teachings spread throughout the Roman Empire, with prominent Stoics such as Seneca, Epictetus, and Emperor Marcus Aurelius contributing to its foundational texts.</p>
          <p>At its core, Stoicism teaches the development of self-control, rationality, and virtue as a means to achieve a tranquil and contented life. Stoics believe that we cannot control external events — only our responses to them.</p>

          <h2 className="text-white text-2xl" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Key Principles</h2>
          <ul className="space-y-2">
            <li><strong className="text-white">Virtue is the only good</strong> — Wisdom, courage, justice, and temperance are the pillars of a fulfilled life.</li>
            <li><strong className="text-white">Control the controllable</strong> — Focus on your thoughts, actions, and emotions, not external events.</li>
            <li><strong className="text-white">Embrace challenges</strong> — Hardships are opportunities to practice virtue and grow stronger.</li>
          </ul>
        </div>

        {/* Interactive: Stoic Self-Assessment */}
        <div className="border border-[#00F0FF]/30 bg-[#00F0FF]/5 p-6 mb-10" data-testid="stoic-self-assessment">
          <div className="flex items-center gap-2 mb-1">
            <Shield className="w-5 h-5 text-[#00F0FF]" />
            <h3 className="text-white text-xl" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Stoic Self-Assessment</h3>
          </div>
          <p className="text-zinc-500 text-xs mb-6">Rate yourself honestly on each principle to discover your stoic score.</p>

          <div className="space-y-4">
            {STOIC_PRINCIPLES.map(p => (
              <div key={p.id} className="border border-zinc-800 bg-zinc-900/60 p-4" data-testid={`stoic-q-${p.id}`}>
                <div className="flex items-start gap-3 mb-3">
                  <p.icon className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: p.color }} />
                  <div>
                    <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: p.color }}>{p.label}</span>
                    <p className="text-zinc-300 text-sm mt-1">{p.question}</p>
                  </div>
                </div>
                <div className="flex gap-2 ml-7">
                  {OPTIONS.map(o => (
                    <button key={o.label}
                      onClick={() => setAnswers(prev => ({ ...prev, [p.id]: o.score }))}
                      className={`flex-1 py-2 text-xs border transition-all ${answers[p.id] === o.score ? 'border-[#00F0FF]/60 bg-[#00F0FF]/10 text-white' : 'border-zinc-800 text-zinc-500 hover:text-white hover:border-zinc-700'}`}
                      data-testid={`stoic-opt-${p.id}-${o.score}`}>
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {allAnswered && !showResults && (
            <button onClick={() => setShowResults(true)}
              className="w-full mt-6 py-3 bg-[#00F0FF] text-black text-sm uppercase tracking-wider hover:opacity-90 transition-opacity"
              style={{ fontFamily: 'Bebas Neue, sans-serif' }}
              data-testid="stoic-reveal-btn">
              Reveal My Stoic Score
            </button>
          )}

          {showResults && (
            <div className="mt-6 border border-zinc-800 bg-zinc-900/60 p-6 text-center" data-testid="stoic-result">
              <span className="text-5xl font-bold" style={{ fontFamily: 'Bebas Neue, sans-serif', color: tier.color }}>{score}</span>
              <span className="text-zinc-600 text-2xl" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>/{maxScore}</span>
              <p className="text-sm uppercase tracking-wider mt-2 mb-3" style={{ fontFamily: 'Bebas Neue, sans-serif', color: tier.color }}>{tier.label}</p>
              <p className="text-zinc-400 text-sm max-w-md mx-auto mb-4">{tier.desc}</p>
              <div className="flex items-center justify-center gap-3">
                <ShareResult
                  text={`I scored ${score}/${maxScore} on the Stoic Self-Assessment — I'm a ${tier.label}! How stoic are you?`}
                  url="https://ninjathlete.com/blogs/article/what-is-a-stoic-person"
                  label="Share Score"
                />
                <button onClick={() => { setAnswers({}); setShowResults(false); }}
                  className="px-4 py-2 border border-zinc-700 text-zinc-400 text-xs uppercase tracking-wider hover:text-white transition-colors"
                  data-testid="stoic-retake">Retake</button>
              </div>
            </div>
          )}
        </div>

        {/* Interactive: Daily Stoic Practice */}
        <div className="border border-[#f59e0b]/30 bg-[#f59e0b]/5 p-6 mb-10" data-testid="daily-stoic-practice">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Sun className="w-5 h-5 text-[#f59e0b]" />
              <h3 className="text-white text-xl" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Today's Stoic Practice</h3>
            </div>
            <button onClick={refreshPractice} className="text-zinc-500 hover:text-[#f59e0b] transition-colors" data-testid="refresh-practice">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
          <div className="bg-zinc-900/60 border border-zinc-800 p-5">
            <span className="text-[10px] uppercase tracking-wider text-[#f59e0b] font-bold">{dailyPractice.category}</span>
            <h4 className="text-white text-lg mt-1 mb-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>{dailyPractice.title}</h4>
            <p className="text-zinc-400 text-sm leading-relaxed">{dailyPractice.desc}</p>
          </div>
        </div>

        {/* Continue Article */}
        <div className="prose-ninja space-y-6 text-zinc-300 leading-relaxed mb-10">
          <h2 className="text-white text-2xl" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Characteristics of a Stoic Person</h2>
          <ul className="space-y-2">
            <li><strong className="text-white">Calm Amidst Chaos</strong> — Remains composed under pressure, seeing challenges as natural growth opportunities.</li>
            <li><strong className="text-white">Rationality Over Emotion</strong> — Doesn't suppress emotions but ensures actions align with core virtues.</li>
            <li><strong className="text-white">Lives According to Nature</strong> — Recognizes one's place in the larger universe and accepts natural order.</li>
            <li><strong className="text-white">Pursuit of Personal Growth</strong> — Every day is an opportunity for self-improvement.</li>
          </ul>

          <h2 className="text-white text-2xl" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Modern Stoicism</h2>
          <p>Mindfulness aligns closely with Stoic teachings. By being present, we can better control our reactions. Given the unpredictable nature of our modern world, Stoicism offers a way to build resilience by focusing on what's within our control and viewing challenges as opportunities.</p>

          <h2 className="text-white text-2xl" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Conclusion</h2>
          <p>Being stoic isn't about suppressing emotions or being indifferent. It's about understanding the transient nature of external events and recognizing the eternal power of our inner virtues. In embracing Stoicism, we can find a roadmap to a more meaningful, virtuous, and contented life.</p>
        </div>

        {/* Cross-links */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Link to="/blogs/article/how-to-practice-stoicism" className="group bg-zinc-900/80 border border-zinc-800 hover:border-[#00F0FF]/40 p-4 transition-all" data-testid="crosslink-practice-stoicism">
            <h4 className="text-white text-sm group-hover:text-[#00F0FF] transition-colors" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>How to Practice Stoicism</h4>
            <p className="text-zinc-600 text-xs mt-1">Cultivate inner virtues daily</p>
          </Link>
          <Link to="/blogs/article/mental-toughness-quotes" className="group bg-zinc-900/80 border border-zinc-800 hover:border-[#E63946]/40 p-4 transition-all" data-testid="crosslink-mental-toughness">
            <h4 className="text-white text-sm group-hover:text-[#E63946] transition-colors" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Mental Toughness Quotes</h4>
            <p className="text-zinc-600 text-xs mt-1">Build resilience through wisdom</p>
          </Link>
        </div>
      </div>
    </main>
  );
}
